import { useUser } from '@clerk/react';
import { useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useEffect, useRef, useState } from 'react';
import { SignupLinkService } from '../services/signup-link/signup-link.service';
import type { ISignupLinkService } from '../services/signup-link/signup-link.service.interface';
import { useValidatedTenantId } from './auth/use-validated-tenant-id';
import { subscriptionQueryKeys } from './subscription/query-keys';
import { onboardingQueryKeys } from './use-onboarding';

/**
 * sessionStorage key holding the signup-link token from landing until the claim
 * lands. Written by the signup-link page, read here as the recovery fallback.
 * Stored as JSON `{ token, ts }` so it can self-expire (see PENDING_TTL_MS) and
 * be bound to the account created after that landing. sessionStorage (not
 * localStorage) on purpose: it is scoped to the tab, so a second tab landing on
 * a different signup link cannot overwrite this tab's token and steal the
 * attribution (wrong consultant, wrong trial length). Every signup flow the
 * fallback exists for — email-code bounce, OAuth redirect — stays in the same
 * tab, so nothing is lost by the narrower scope.
 */
export const PENDING_SIGNUP_LINK_TOKEN_KEY = 'pending-signup-link-token';

// The fallback must behave like Clerk's unsafeMetadata did: short-lived and
// bound to the person who actually used the link. Without these guards the raw
// localStorage token could be replayed onto ANY eligible tenant that later logs
// in on the same browser (wrong-tenant association) or onto a pre-existing
// TRIALING tenant (whose trial would be recomputed from createdAt and could land
// in the past → instant expiry).
// Both windows match the backend's CLAIM_TENANT_FRESHNESS_MS: a shorter client
// window would refuse recoveries the server would still honour — exactly the
// "read the verification email an hour later" case PRSP-241 is about.
const PENDING_TTL_MS = 2 * 60 * 60 * 1000;
const FRESH_USER_MS = 2 * 60 * 60 * 1000;
// `ts` comes from the browser clock and `user.createdAt` from Clerk's servers,
// so the binding below needs slack for ordinary skew.
const CLOCK_SKEW_TOLERANCE_MS = 5 * 60 * 1000;

interface PendingSignupLink {
  token: string;
  /** Landing timestamp (browser clock), used for the TTL and the account binding. */
  ts: number;
}

/**
 * Web Storage throws (not returns null) when unavailable: Safari private mode
 * quota, a partitioned third-party context, a user blocking site data. The
 * fallback is a best-effort extra, so every access degrades to "no fallback"
 * instead of taking the signup flow down with it. The claimed flag stays in
 * localStorage (it must survive the tab); the pending token lives in
 * sessionStorage (it must NOT leak across tabs).
 */
function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: nothing to persist, the claim still ran.
  }
}

function safeSessionGetItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSetItem(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable: nothing to persist, the claim still ran.
  }
}

function safeSessionRemoveItem(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable: there is nothing to clean up.
  }
}

function normalizeToken(raw: unknown): string | null {
  return typeof raw === 'string' && raw.length > 0 && raw.length <= 32 ? raw : null;
}

/** Persist the token for post-auth recovery. Overwrites any stale pending token of this tab. */
export function writePendingSignupLinkToken(token: string): void {
  safeSessionSetItem(PENDING_SIGNUP_LINK_TOKEN_KEY, JSON.stringify({ token, ts: Date.now() }));
}

/** Read the pending entry, dropping it if malformed or past its TTL. */
function readPendingSignupLink(): PendingSignupLink | null {
  const raw = safeSessionGetItem(PENDING_SIGNUP_LINK_TOKEN_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as { token?: unknown; ts?: unknown };
    const token = normalizeToken(parsed.token);
    const ts = typeof parsed.ts === 'number' ? parsed.ts : null;
    if (!token || ts === null || Date.now() - ts > PENDING_TTL_MS) {
      safeSessionRemoveItem(PENDING_SIGNUP_LINK_TOKEN_KEY);
      return null;
    }
    return { token, ts };
  } catch {
    safeSessionRemoveItem(PENDING_SIGNUP_LINK_TOKEN_KEY);
    return null;
  }
}

/**
 * Read the fallback token. Exported so callers outside this hook (e.g. the
 * onboarding wizard) can tell whether a signup-link claim is still in flight
 * before doing something that shouldn't happen for a free-trial user (like
 * creating a real Stripe PaymentIntent).
 */
export function readPendingSignupLinkToken(): string | null {
  return readPendingSignupLink()?.token ?? null;
}

/**
 * Deterministic recovery for two ways the signup-link token goes missing at
 * `user.created`:
 *   1. The metadata-copy race — `user.created` fires before Clerk copies
 *      `SignUp.unsafeMetadata → User.unsafeMetadata`.
 *   2. The email-code bounce — Clerk's verification-code step restarts the flow
 *      on another route, so the completing SignUp carries no unsafeMetadata and
 *      the token never reaches `User.unsafeMetadata` at all.
 * Case 1 is recoverable from Clerk metadata (already user-bound + fresh); case 2
 * is not, so we fall back to a localStorage token the landing page persisted.
 * That fallback is only consumed for an account created AFTER that landing and
 * within a TTL, matching the safety unsafeMetadata gave for free. Once
 * authenticated we re-play the token to the backend, which associates + counts
 * idempotently and only touches an eligible TRIALING tenant of which the caller
 * is the owner.
 */
export interface SignupLinkClaimResult {
  /**
   * True while a claim may still land for this tab: a pending fallback token is
   * waiting for the bootstrap, or the claim request is in flight. Settles to
   * false on every terminal outcome — claimed, not-claimed, network error — so
   * consumers (the onboarding payment gate) never wait on it forever. Reactive
   * on purpose: the wizard must re-run its payment-setup effect when this
   * flips, which a raw localStorage read can never trigger.
   */
  isClaimPending: boolean;
}

export function useSignupLinkClaim(): SignupLinkClaimResult {
  const { user } = useUser();
  const service = useInjection<ISignupLinkService>(SignupLinkService);
  const tenantId = useValidatedTenantId();
  const queryClient = useQueryClient();
  const attemptedRef = useRef(false);
  // A pending token found at mount means a claim is owed as soon as the
  // bootstrap validates the tenant — start pending so the payment gate holds
  // from the first render instead of racing the effect below.
  const [isClaimPending, setIsClaimPending] = useState(() => readPendingSignupLinkToken() !== null);

  useEffect(() => {
    // Wait for the validated tenant. Firing at first render — before the
    // auto-select has written `tenant_context` — sends no `x-tenant-id`, and the
    // guard answers 401: the global ky hook reads that as a dead session and
    // signs the brand-new user out (PRSP-241). It would also be pointless, since
    // the server claim is scoped to the caller's tenant. `tenantId` is a
    // dependency so the claim fires the moment the bootstrap validates it.
    if (!user || !tenantId || attemptedRef.current) {
      return;
    }

    // Both sources are gated on the account being newly created. Recovery only
    // makes sense at signup time (the metadata-copy race and the email-code
    // bounce both happen within minutes), so a returning account has nothing to
    // claim — and gating it here prevents two failure modes: associating a
    // returning user's tenant on a shared browser (fallback), and an unbounded
    // per-page-load retry on secondary devices where the token lingers in Clerk
    // metadata but the tenant is already associated (metadata → claimed:false).
    const createdMs = user.createdAt?.getTime();
    const isFreshAccount = typeof createdMs === 'number' && Date.now() - createdMs <= FRESH_USER_MS;
    if (!isFreshAccount) {
      // A non-fresh account never attempts recovery, so a pending token left
      // over here (shared browser, stale tab) will never be consumed by this
      // hook — drop it now instead of letting it block this tenant's payment
      // step until the TTL.
      safeSessionRemoveItem(PENDING_SIGNUP_LINK_TOKEN_KEY);
      setIsClaimPending(false);
      return;
    }

    const token =
      normalizeToken(user.unsafeMetadata?.signupLinkToken) ?? readFallbackToken(createdMs);
    if (!token) {
      setIsClaimPending(false);
      return;
    }

    // Keyed by user AND token: a multi-registration link used from the same
    // device (restaurant tablet, a consultant signing up several clients) must
    // let every account claim its own slot. A per-token flag made everyone after
    // the first skip the claim and land on the credit-card step.
    const storageKey = `signup-link-claimed:${user.id}:${token}`;
    if (safeGetItem(storageKey) === '1') {
      attemptedRef.current = true;
      safeSessionRemoveItem(PENDING_SIGNUP_LINK_TOKEN_KEY);
      setIsClaimPending(false);
      return;
    }

    attemptedRef.current = true;
    setIsClaimPending(true);
    service
      .claim(token)
      .then((claimed) => {
        // `claimForTenant` only returns `false` for definitive reasons (token
        // unknown, tenant already associated / not TRIALING / outside the
        // freshness window / caller not the owner, link revoked/expired) — never
        // "not yet provisioned, try again" now that we wait for a validated
        // tenant before firing. Both answers are therefore terminal: flag the
        // attempt so a remount doesn't re-POST it, and drop the pending token so
        // it stops blocking the payment step (see `hasPendingSignupLinkClaim` in
        // onboarding-state.ts) until the TTL.
        safeSetItem(storageKey, '1');
        safeSessionRemoveItem(PENDING_SIGNUP_LINK_TOKEN_KEY);
        if (claimed) {
          // The association we just wrote is what makes onboarding skip the
          // payment step and the trial end date shift. Both are already cached
          // (status has a 5min staleTime), so without this the wizard keeps the
          // pre-claim answer and asks a free-trial user for a card.
          queryClient.invalidateQueries({ queryKey: onboardingQueryKeys.statusRoot() });
          queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.all });
        }
        setIsClaimPending(false);
      })
      .catch(() => {
        // Not a definitive answer: keep the token and leave the claimed flag
        // unset so the NEXT mount retries, but settle the pending state so the
        // payment step never waits forever. attemptedRef deliberately stays
        // true: it is per-mount anyway (a remount starts fresh), and resetting
        // it here would let any dep identity churn re-POST in a loop.
        setIsClaimPending(false);
      });
  }, [user, service, tenantId, queryClient]);

  return { isClaimPending };
}

/**
 * The pending token belongs to whoever opened the landing page last. An account
 * that already existed at that moment did not come from that link, so it must
 * not consume it — the token is dropped instead of lingering until the TTL.
 */
function readFallbackToken(accountCreatedMs: number): string | null {
  const pending = readPendingSignupLink();
  if (!pending) {
    return null;
  }
  if (accountCreatedMs < pending.ts - CLOCK_SKEW_TOLERANCE_MS) {
    safeSessionRemoveItem(PENDING_SIGNUP_LINK_TOKEN_KEY);
    return null;
  }
  return pending.token;
}
